import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

/**
 * Single seam for telemetry. The rest of the codebase uses these four
 * functions; nothing else imports `@sentry/react` or `posthog-js`.
 *
 * Both clients are initialised lazily by `initObservability`, gated on
 * their respective env vars. Local development (where the env vars are
 * unset) leaves both clients uninitialised and every public function is
 * a no-op — no events leave the machine.
 */

interface ObservabilityConfig {
  sentryDsn?: string;
  posthogKey?: string;
  posthogHost?: string;
  /** Forwarded to Sentry as `release`; release-please's tag drives this
   *  in production. Optional in local dev. */
  release?: string;
  /** Tag attached to every event for filtering by deploy target. */
  environment?: string;
}

let sentryReady = false;
let posthogReady = false;

export function initObservability(config: ObservabilityConfig): void {
  if (config.sentryDsn) {
    Sentry.init({
      dsn: config.sentryDsn,
      release: config.release,
      environment: config.environment,
      // Keep the bundle quiet for a personal tool — no replay, no
      // performance traces. Errors only.
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    });
    sentryReady = true;
  }
  if (config.posthogKey) {
    posthog.init(config.posthogKey, {
      api_host: config.posthogHost ?? 'https://eu.i.posthog.com',
      // Autocapture is on by default; explicit `track()` events
      // supplement DOM-level click capture. Disable session recording
      // — separate decision per the PRD.
      capture_pageview: true,
      disable_session_recording: true,
    });
    // Tag every event (autocapture + explicit track()) with the
    // environment so dashboards can filter local-dev verification
    // runs out of production analytics. Sentry has a top-level
    // `environment` field; PostHog uses super-properties.
    if (config.environment) {
      posthog.register({ environment: config.environment });
    }
    posthogReady = true;
  }
}

export interface UserIdentity {
  id: string;
  email: string;
}

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
};

/**
 * Forwards a thrown value to Sentry. Coerces non-Error inputs into
 * Errors first so stack-trace handling stays consistent. The optional
 * `context` lands as Sentry's `extra` block. No-op when Sentry is not
 * initialised (local dev / missing DSN).
 */
export function captureError(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!sentryReady) return;
  Sentry.captureException(toError(err), context ? { extra: context } : undefined);
}

/**
 * Forwards a named event with optional properties to PostHog. No-op
 * when PostHog is not initialised. Used for the curated taxonomy of
 * canvas-internal and milestone-shaped events that autocapture can't
 * see.
 */
export function track(
  event: string,
  props?: Record<string, unknown>,
): void {
  if (!posthogReady) return;
  posthog.capture(event, props);
}

/**
 * Tags subsequent events in both clients with the authenticated user.
 * Called once per session immediately after the auth callback resolves.
 */
export function identifyUser(user: UserIdentity): void {
  if (sentryReady) Sentry.setUser({ id: user.id, email: user.email });
  if (posthogReady) posthog.identify(user.id, { email: user.email });
}

/**
 * Clears identity in both clients. Called on sign-out so identity
 * doesn't leak across sessions on a shared machine.
 */
export function resetIdentity(): void {
  if (sentryReady) Sentry.setUser(null);
  if (posthogReady) posthog.reset();
}

/**
 * Test-only helper. Resets the module's internal initialisation flags
 * so a test can re-run `initObservability` against fresh mocks
 * without leaking state across tests.
 *
 * @internal
 */
export function __resetObservabilityForTests(): void {
  sentryReady = false;
  posthogReady = false;
}
